import { useState, useRef, useCallback, useId } from 'react';
import { getAllRegions } from '../../utils/regionMapper';
import { usePopoverDismiss } from '../../hooks/usePopoverDismiss';
import './RegionFilterPopover.css';

interface RegionFilterPopoverProps {
  selectedRegions: string[];
  onRegionsChange: (regions: string[]) => void;
  regionCounts: Record<string, number>;
}

export function RegionFilterPopover({
  selectedRegions,
  onRegionsChange,
  regionCounts,
}: RegionFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const allRegions = getAllRegions();
  const popoverId = useId();

  usePopoverDismiss(open, wrapperRef, useCallback(() => setOpen(false), []));

  const toggleRegion = (regionName: string) => {
    if (selectedRegions.includes(regionName)) {
      onRegionsChange(selectedRegions.filter((r) => r !== regionName));
    } else {
      onRegionsChange([...selectedRegions, regionName]);
    }
  };

  const selectAll = () => onRegionsChange(allRegions.map((r) => r.name));
  const clearAll = () => onRegionsChange([]);
  const count = selectedRegions.length;

  return (
    <div className="region-popover-wrapper" ref={wrapperRef}>
      <button
        className={`filter-trigger ${count > 0 ? 'active' : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={popoverId}
      >
        <span className="filter-trigger-label">지역</span>
        {count > 0 && <span className="filter-trigger-count">{count}</span>}
        <span className={`filter-trigger-chevron ${open ? 'open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="filter-popover" id={popoverId} role="dialog" aria-label="지역 필터">
          <div className="filter-popover-header">
            <span className="filter-popover-title eyebrow">지역 선택</span>
            <div className="filter-popover-actions">
              <button onClick={selectAll}>전체</button>
              <button onClick={clearAll} disabled={count === 0}>
                초기화
              </button>
            </div>
          </div>
          <ul className="filter-popover-list region-popover-list">
            {allRegions.map((region) => {
              const id = `${popoverId}-${region.code}`;
              return (
                <li key={region.code} className="filter-popover-item region-popover-item">
                  <input
                    id={id}
                    type="checkbox"
                    checked={selectedRegions.includes(region.name)}
                    onChange={() => toggleRegion(region.name)}
                  />
                  <label htmlFor={id} className="filter-popover-label region-label">
                    <span className="region-label-name">{region.label}</span>
                    <span className="region-label-count">{regionCounts[region.name] ?? 0}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
